import styled, { useTheme } from "styled-components";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import EditOutlined from "@ant-design/icons/EditOutlined";
import React, { useContext, useEffect, useState } from "react";
import { GeneralConfigurationContext } from "features/configuration/GeneralConfiguration/generalConfigurationContexts";
import Text from "common/Typography/Text";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import { hexToRGB } from "@shared/utils/misc";
import { openMessage } from "utils/misc";
import useDebouncedCallback from "@shared/utils/hooks/useDebouncedCallback";
import { ConfigurationItemRowProps } from "@shared/features/configuration/types";
import Radio from "common/Radio/Radio";
import { useSubUserPermissions } from "@shared/utils/hooks/misc.ts";

const ConfigurationItemRow = ({
  nameComponent,
  extraInfoComponent,
  deleteHandler,
  overrideComponent,
  onClickRow,
  inactive,
  selected,
  extraInfoAlignRight,
  editHandler,
  itemValueModified,
  isFocused,
  validationMessage,
  isError,
  innerComponent,
  defaultExpanded,
  expanded,
  onExpandChange,
  type,
  expandExtraInfoOnClickRow,
  wrapperId,
  errorMessage,
  extraInfoComponentFlex,
  isColoredIcons,
  showRadioButton,
  setItemToEdit,
  hideEditAndRemove,
  style,
}: ConfigurationItemRowProps) => {
  const {
    showOverrides,
    colorModifiedValues,
    extraInfoAlignRight: contextAlignExtraInfoRight,
    useSimpleLayout,
    editable,
    addEditWarningMessage,
  } = useContext(GeneralConfigurationContext)!;
  const appTheme = useTheme();
  const { isReadOnly } = useSubUserPermissions();

  const [localExpanded, setLocalExpanded] = useState(
    expanded ?? defaultExpanded
  );
  const isExpanded = expanded ?? localExpanded;
  const toggleIsExpanded = () => {
    setLocalExpanded(!isExpanded);
    onExpandChange?.(!isExpanded);
  };

  const onClick = () => {
    if (errorMessage) {
      openMessage(errorMessage, "error");
    } else if (innerComponent || expandExtraInfoOnClickRow) {
      toggleIsExpanded();
    }
  };

  const getEditWarningMessage = () => {
    if (addEditWarningMessage && !editable && !isReadOnly) {
      openMessage(
        "Please enable edit mode before trying to make changes",
        "warning"
      );
    }
  };

  const debouncedGetEditWarningMessage = useDebouncedCallback({
    callback: getEditWarningMessage,
    wait: 5000,
    leading: true,
  });

  let extraInfoComponentWithExpand = extraInfoComponent;
  if (
    expandExtraInfoOnClickRow &&
    extraInfoComponent &&
    React.isValidElement(extraInfoComponent)
  ) {
    extraInfoComponentWithExpand = React.cloneElement(
      extraInfoComponent as React.ReactElement<any>,
      {
        expanded: isExpanded,
        setExpanded: setLocalExpanded,
      }
    );
  }

  useEffect(() => {
    if (inactive && isExpanded) {
      toggleIsExpanded();
    }
  }, [inactive]);

  const coloredIconStyle = isColoredIcons && {
    color: appTheme.colors.primaryBlue,
  };

  return (
    <ItemWrapper
      onClick={onClickRow}
      $inactive={inactive}
      $showPointer={editable && !!onClickRow && !inactive}
      $isFocused={isFocused}
      id={wrapperId}
    >
      <ItemInfoWrapper
        onClick={onClick}
        $showPointer={
          editable && !inactive && (expandExtraInfoOnClickRow || innerComponent)
        }
        style={style}
      >
        {showRadioButton && <Radio checked={selected} />}
        {!!nameComponent && (
          <NameComponentWrapper $type={type || (useSimpleLayout && "simple")}>
            {nameComponent}
          </NameComponentWrapper>
        )}
        {extraInfoComponent && (
          <ExtraInfoComponentWrapper
            $alignRight={extraInfoAlignRight || contextAlignExtraInfoRight}
            $itemValueModified={colorModifiedValues && itemValueModified}
            $flex={extraInfoComponentFlex}
            onClick={debouncedGetEditWarningMessage}
          >
            {extraInfoComponentWithExpand}
          </ExtraInfoComponentWrapper>
        )}
        {overrideComponent && showOverrides && (
          <OverrideComponentWrapper onClick={debouncedGetEditWarningMessage}>
            {overrideComponent}
          </OverrideComponentWrapper>
        )}
        {!hideEditAndRemove && (
          <>
            {editHandler && (
              <EditOutlined
                onClick={(e) => {
                  editHandler();
                  setItemToEdit?.();
                  e.stopPropagation();
                }}
                style={{
                  marginLeft: 16,
                  fontSize: appTheme.typography.fontSizes.L,
                  ...coloredIconStyle,
                }}
              />
            )}
            {deleteHandler && (
              <CloseOutlined
                onClick={(e) => {
                  deleteHandler();
                  e.stopPropagation();
                }}
                style={{
                  marginLeft: 10,
                  fontSize: 13,
                  ...coloredIconStyle,
                }}
              />
            )}
          </>
        )}
      </ItemInfoWrapper>
      {innerComponent && (
        <ExpandCollapse
          expanded={!inactive && !!isExpanded}
          style={{ width: "100%" }}
        >
          {innerComponent}
        </ExpandCollapse>
      )}
      {validationMessage && !inactive && (
        <Text
          type={isError ? "danger" : "secondary"}
          style={{ alignSelf: "flex-end" }}
        >
          {validationMessage}
        </Text>
      )}
    </ItemWrapper>
  );
};

export default ConfigurationItemRow;

const MOBILE_BREAKPOINT = "500px";
const NameComponentWrapper = styled.div<{ $type?: "simple" }>`
  padding-right: 20px;
  flex: ${(p) => (p.$type === "simple" ? "none" : "1 1 180px")};
  user-select: none;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: none;
    width: 140px;
  }
`;
const ExtraInfoComponentWrapper = styled.div<{
  $alignRight?: boolean;
  $itemValueModified?: boolean;
  $flex?: string;
}>`
  display: flex;
  align-items: center;
  ${(props) =>
    props.$alignRight &&
    `
    justify-content: flex-end;
  `};
  ${(props) =>
    props.$itemValueModified &&
    `
    color: ${props.theme.colors.primaryBlue};
    font-weight: 500;
    & div, & input {
      color: ${props.theme.colors.primaryBlue};
      font-weight: 500;
    }
    .ant-input-number-input{
      color: ${props.theme.colors.primaryBlue};
    }
  `};
  flex: ${(p) => p.$flex || "1 1 150px"};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: auto;
  }
`;
const OverrideComponentWrapper = styled.div`
  width: 45px;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 36px;
    display: flex;
    justify-content: end;
    align-items: center;
  }
`;
const ItemWrapper = styled.div<{
  $inactive?: boolean;
  $showPointer?: boolean;
  $isFocused?: boolean;
}>`
  border-bottom: ${(p) => p.theme.borders.light};
  padding: 6px 0;
  transition: color 0.35s;
  position: relative;
  height: auto;
  min-height: 45px;
  display: flex;
  flex-flow: column;
  align-items: flex-end;
  justify-content: center;
  color: ${(p) => p.theme.colors.textColor};
  ${(props) =>
    props.$inactive &&
    `
    color: ${hexToRGB(props.theme.colors.secondaryText, 0.5)};
    cursor: auto !important;
  `}
  ${(props) =>
    props.$showPointer &&
    `
    cursor: pointer;
  `}
  ${(props) =>
    props.$isFocused &&
    `
    border-bottom: 1px solid
      ${props.theme.isNewLook ? props.theme.colors.primaryBlue : "#3272FE"};
  `}
`;
const ItemInfoWrapper = styled.div<{ $showPointer?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  ${(p) =>
    p.$showPointer &&
    `
    cursor: pointer;
  `}
`;
